import { PlusIcon, TrashIcon } from '@slal/ui/icons';
import { FormProvider, yupResolver } from '@slal/ui/react-hook-form/core';
import { CheckboxField, Field } from '@slal/ui/react-hook-form/fields';
import { P } from '@slal/ui/typography';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { BackButton, MaxWidthContent, StyledForm, StyledFormIntroduction } from '~/components';
import { useApplicationForm } from '~/hooks';
import { pensionDeclarationValidationSchema } from '~/schemas';
import { ApplicationFormKeys, Pension, PensionDeclarationSchema } from '~/types';
import { formatToGBP } from '~/utils';
import { StyledDescriptionList } from '../ReviewApplication/styles';
import { SubmitButton } from '../SubmitButton';
import { StyledApplicationButtonsWrapper } from '../styles';
import { DeclarationModal } from './DeclarationModal';
import {
    AddPensionCard,
    PensionCard,
    StyledDeclarationLink,
    StyledDeclarationsLabel,
    StyledPensionActions,
} from './styles';
import { initialDeclaration } from './utils';

const ExistingPensions = ({ pensions, onRemove }: { pensions: Pension[]; onRemove: (index: number) => void }) => {
    return (
        <>
            {pensions.map(({ policyNumber, value }, index) => (
                <PensionCard key={index}>
                    <StyledDescriptionList
                        className="no-hr"
                        items={[
                            {
                                label: 'Policy number',
                                value: policyNumber,
                            },
                            {
                                label: 'Current value',
                                value: formatToGBP(value),
                            },
                        ]}
                    />
                    <StyledPensionActions>
                        <TrashIcon role="button" title="Remove this pension" onClick={() => onRemove(index)} />
                    </StyledPensionActions>
                </PensionCard>
            ))}
        </>
    );
};

const DeclarationLabel = () => {
    const [isDeclarationModalOpen, setIsDeclarationModalOpen] = useState(false);
    const closeModal = useCallback(() => setIsDeclarationModalOpen(false), [setIsDeclarationModalOpen]);
    const openModal = useCallback(() => setIsDeclarationModalOpen(true), [setIsDeclarationModalOpen]);

    return (
        <>
            <StyledDeclarationsLabel tag="span">
                I have checked my{' '}
                <StyledDeclarationLink type="button" onClick={openModal}>
                    valuable features and guarantees
                </StyledDeclarationLink>{' '}
                and am happy to continue.
            </StyledDeclarationsLabel>
            <DeclarationModal isOpen={isDeclarationModalOpen} onClose={closeModal} />
        </>
    );
};

export const PensionDeclarationForm = () => {
    const { handleSubmit, form } = useApplicationForm<PensionDeclarationSchema>(
        {
            defaultValues: initialDeclaration,
            resolver: yupResolver(pensionDeclarationValidationSchema),
        },
        ApplicationFormKeys.PENSION_DECLARATION
    );

    const navigate = useNavigate();

    const onValidCallback = () => navigate('/application/withdrawal-options');

    return (
        <MaxWidthContent>
            <FormProvider {...form}>
                <StyledForm onSubmit={handleSubmit(onValidCallback)}>
                    <Field errorFor="pensionDeclaration.declaration">
                        <CheckboxField label={<DeclarationLabel />} name="pensionDeclaration.declaration" />
                    </Field>
                    <StyledApplicationButtonsWrapper>
                        <SubmitButton />
                        <BackButton route={'/application/additional-details'} />
                    </StyledApplicationButtonsWrapper>
                </StyledForm>
            </FormProvider>
        </MaxWidthContent>
    );
};

export const Pensions = ({
    pensions,
    onRemovePension,
}: {
    pensions: Pension[];
    onRemovePension: (index: number) => void;
}) => {
    const navigate = useNavigate();

    return (
        <MaxWidthContent>
            {!!pensions?.length && (
                <>
                    <StyledFormIntroduction>
                        <P>
                            You can transfer pensions from other providers after your new Standard Life pension is set
                            up.
                        </P>
                    </StyledFormIntroduction>
                    <ExistingPensions pensions={pensions} onRemove={onRemovePension} />
                </>
            )}
            {pensions?.length < 5 && (
                <AddPensionCard startIcon={<PlusIcon />} onClick={() => navigate('/application/add-pension')}>
                    {pensions?.length ? 'Add another ReAssure pension' : 'Add a ReAssure pension'}
                </AddPensionCard>
            )}
            {pensions.length ? (
                <PensionDeclarationForm />
            ) : (
                <StyledApplicationButtonsWrapper>
                    <BackButton route={'/application/additional-details'} />
                </StyledApplicationButtonsWrapper>
            )}
        </MaxWidthContent>
    );
};
